import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { handleCallback } from '../lib/auth';
import { apiClient } from '../lib/api';

/**
 * OIDC redirect landing page. The IdP redirects here after authentication; we complete the
 * Authorization Code + PKCE exchange, persist the access token, invalidate the cached user,
 * and route into the app. Reachable while unauthenticated (registered before the auth gate).
 */
export function AuthCallback() {
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await handleCallback();
        if (cancelled) return;
        // The IdP did the authenticating, so the server never saw a login. Tell it, so the
        // admin login log covers OIDC sign-ins too. Best-effort: a failure here must not
        // block a sign-in that already succeeded.
        try {
          await apiClient.post('/api/auth/session', {});
        } catch {
          // ignore — the user is signed in either way
        }
        if (cancelled) return;
        await qc.invalidateQueries({ queryKey: ['me'] });
        navigate('/', { replace: true });
      } catch (ex) {
        if (cancelled) return;
        setErr(ex instanceof Error ? ex.message : 'Sign-in could not be completed.');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, qc]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm text-center">
        {err === null ? (
          <p className="text-slate-500 text-sm">Completing sign-in...</p>
        ) : (
          <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
            <h1 className="text-lg font-bold text-brand-charcoal mb-2">Sign-in failed</h1>
            <p className="text-sm text-slate-600 mb-4">{err}</p>
            <Link to="/login" className="text-brand-blue hover:underline text-sm">Back to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
